import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { analyticsService, LeaderboardEntry } from '../../services/analyticsService';
import { CHART_COLORS } from '../../utils/chartColors';

interface MeetingsPerRepChartProps {
  businessIdentifier?: string;
  timeRange?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

const truncateName = (name: string, maxLen: number = 12): string => {
  if (name.length <= maxLen) return name;
  return name.slice(0, maxLen) + '...';
};

const MeetingsPerRepChart: React.FC<MeetingsPerRepChartProps> = ({
  businessIdentifier,
  timeRange = '7d',
}) => {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await analyticsService.getSalesLeaderboard(
          businessIdentifier,
          timeRange,
          'total_conversations',
          20
        );
        setEntries(response.leaderboard || []);
      } catch (err: any) {
        console.error('[MeetingsPerRepChart] Error fetching data:', err);
        setError(err.message || 'Failed to load meetings data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [businessIdentifier, timeRange]);

  const getChartOption = () => {
    if (entries.length === 0) return {};

    const names = entries.map((e) => truncateName(e.salesperson_name));
    const values = entries.map((e) => e.total_conversations);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        textStyle: { fontFamily: FONT_FAMILY },
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex;
          const entry = entries[idx];
          if (!entry) return '';
          return `<b>${entry.salesperson_name}</b><br/>Meetings: ${entry.total_conversations}`;
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: entries.length > 5 ? '30%' : '15%',
        top: '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: names,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 10,
          color: '#666',
          interval: 0,
          rotate: 90,
        },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
      },
      yAxis: {
        type: 'value',
        name: 'Meetings',
        nameTextStyle: { fontFamily: FONT_FAMILY, fontSize: 11, color: '#999' },
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#999',
        },
        splitLine: { lineStyle: { color: '#EDF2F7' } },
      },
      series: [
        {
          type: 'bar',
          data: values,
          barWidth: '50%',
          itemStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: CHART_COLORS.primary },
                { offset: 1, color: CHART_COLORS.primary + '88' },
              ],
            },
            borderRadius: [4, 4, 0, 0],
          },
        },
      ],
    };
  };

  return (
    <ChartCard
      title="Meetings Per Rep"
      subtitle="Total conversations by salesperson"
      isLoading={isLoading}
      error={error}
    >
      {entries.length === 0 && !isLoading ? (
        <Flex justify="center" align="center" minH="200px">
          <Text color="gray.400" fontSize="sm">No data available</Text>
        </Flex>
      ) : (
        <ReactECharts
          option={getChartOption()}
          style={{ height: '380px', width: '100%' }}
          opts={{ renderer: 'canvas' }}
        />
      )}
    </ChartCard>
  );
};

export default MeetingsPerRepChart;
